// Motion in Prospero is feedback, state change, and continuity — never decoration.
// Every new `transition` / `animation` must use these tokens (see `.claude/rules/frontend-theming.md`).

export const MOTION_DURATION_MS = {
  /** Immediate feedback: press, hover, toggle. */
  instant: 150,
  /** State change: color, border, shadow, small reveals. */
  routine: 250,
  /** Layout, overlay, view transition, list entrance. */
  layout: 400,
  /** The single authored entrance of a surface. */
  focal: 650,
} as const;

export type MotionDuration = (typeof MOTION_DURATION_MS)[keyof typeof MOTION_DURATION_MS];

export const MOTION_EASING = {
  /** Entering elements: fast start, long settle (ease-out-expo). */
  arrive: 'cubic-bezier(0.16, 1, 0.3, 1)',
  /** Leaving elements: accelerate away (ease-in-expo). */
  exit: 'cubic-bezier(0.7, 0, 0.84, 0)',
  /** In-place state changes. */
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
} as const;

export type MotionEasing = (typeof MOTION_EASING)[keyof typeof MOTION_EASING];

/** Delay between consecutive items of a staggered list entrance. */
export const MOTION_STAGGER_MS = 60;

export const REDUCED_MOTION_QUERY = '@media (prefers-reduced-motion: reduce)';

// Reduced motion is about movement, not color: `transition` is left alone so
// color/shadow state changes keep easing. Callers animating `transform` via a
// transition must override it under `REDUCED_MOTION_QUERY` themselves.
const REDUCED_MOTION_STYLES = {
  animation: 'none',
  opacity: 1,
  transform: 'none',
} as const;

export function motionTransition(
  properties: readonly string[],
  duration: number = MOTION_DURATION_MS.routine,
  easing: string = MOTION_EASING.standard,
): string {
  return properties.map((property) => `${property} ${duration}ms ${easing}`).join(', ');
}

export function withReducedMotion<T extends object>(
  sx: T,
): T & { [REDUCED_MOTION_QUERY]: typeof REDUCED_MOTION_STYLES } {
  return {
    ...sx,
    [REDUCED_MOTION_QUERY]: REDUCED_MOTION_STYLES,
  };
}
